import { createThemeButtonDataLegacy, createThemeButtonHoverLegacy } from '../common/schemas/legacy/theme-button';
import { createAnalyticsSettingsLegacy } from '../common/schemas/legacy/analytics';
import { createExtendedPositionLegacy } from '../common/schemas/legacy/extended-position';
import { createWidgetPositionLegacy } from '../common/schemas/legacy/widget-position';

import type { FormWidgetEntity, LegacyFormDefault, LegacyFormWidget, LegacyFormZero } from './schemas';
import type { FormButtonSettingsData } from './schemas';

/**
 * Host-only values the package deliberately doesn't own — i18n strings and
 * the default button-icon SVG template. The host model passes them in,
 * mirroring the button widget's `buttonOverrides` convention.
 */
export type CreateLegacyFormWidgetOptions = {
    texts?: {
        /** `crm_deal_name` */
        dealName?: string;
        /** `form_button_text` */
        buttonLabel?: string;
        /** `confirm` */
        modalButtonLabel?: string;
        /** `<h3>${'form_default_notification'}</h3>` */
        subscriptionMessageHtml?: string;
        /** `modal_form_title_text` */
        modalTitle?: string;
        /** `control_form_lead_sourse` */
        leadSource?: string;
    };
    /** Default icon SVG (`buttonIconTemplates.telegram` in the host). */
    buttonIcon?: string;
};

const createFormButtonSettings = (label: string, icon: string): FormButtonSettingsData => ({
    label,
    styleType: 'primary',
    size: 'M',
    buttonBase: createThemeButtonDataLegacy(),
    buttonHover: createThemeButtonHoverLegacy(),
    widthType: 'full',
    horizontalFormWidthType: 'auto',
    widthValue: 260,
    buttonIcon: {
        isActive: false,
        icon,
        position: 'L',
    },
    additionalEffects: {
        isActive: false,
        effectCondition: 'hover',
        hoverEffects: 'bounce',
        staticEffects: 'shine',
    },
    isIndependentCorners: false,
    advancedRadius: {
        borderBottomLeftRadius: null,
        borderBottomRightRadius: null,
        borderTopLeftRadius: null,
        borderTopRightRadius: null,
    },
    advancedBorderSettings: null,
    isShadow: false,
    shadowSettings: null,
});

/**
 * Field values shared by both placement variants — everything except
 * `position` (the only field the variants diverge on).
 */
const createCommonData = (options?: CreateLegacyFormWidgetOptions): Omit<LegacyFormDefault['data'], 'position'> => ({
    selectedAddressBook: null,
    selectedPipeline: null,
    selectedPipelineStep: null,
    selectedPipelineSettings: null,
    createAddressBook: false,
    addChatBotToForm: false,
    addCrmTags: false,
    contactTags: [],
    addAddressBookTags: false,
    contactAddressBookTags: [],
    chatBotsButtonSettings: {
        type: 'icon',
        buttonSize: 'M',
        iconType: 'round',
        styleType: 'secondary',
        isIndependentCorners: false,
        advancedRadius: {
            borderBottomLeftRadius: null,
            borderBottomRightRadius: null,
            borderTopLeftRadius: null,
            borderTopRightRadius: null,
        },
        buttonBase: createThemeButtonDataLegacy(),
        buttonHover: createThemeButtonHoverLegacy(),
    },
    chatBots: [
        {
            socialNetwork: 'telegram',
            type: 'icon',
            iconType: 'outlined',
            name: 'Telegram',
            link: '',
            description: '',
            botName: '',
            botId: '',
            variables: null,
            botChain: {
                chainId: null,
                name: '',
                isDefault: false,
            },
            uri: null,
            phone: null,
        },
    ],
    createContactAndDeal: true,
    dealName: options?.texts?.dealName ?? '',
    elements: [
        {
            type: 'email',
            label: 'Email',
            placeholder: 'username@gmail.com',
            required: true,
            protected: true,
            isDynamicPlaceholderEnabled: false,
            crmContactVariable: null,
            crmDealVariable: null,
            variable: 'email',
            htmlInputType: 'email',
            autocompleteType: 'email',
        },
    ],
    inputSettings: {
        modalBackgroundColor: '#fbfcfc',
        modalBorderRadius: 6,
        modalPadding: 45,
        modalTitleTextColor: '#333',
        modalShowLabels: true,
        modalInputBorderType: 'fullBorder',
        modalInputBackgroundColor: '#fff',
        modalInputBorderColor: '#ddd',
        modalTextColor: '#333',
        modalInputTextColor: '#333',
        modalInputBorderRadius: 3,
        modalInputSize: 'M',
        showLabels: true,
        inputBorderType: 'fullBorder',
        formStyleType: 'vertical',
        backgroundColor: '#fff',
        borderColor: '#ddd',
        textColor: '#333',
        inputTextColor: '#333',
        borderRadius: 3,
        inputSize: 'M',
        horizontalPosition: 'L',
    },
    formButtonSettings: createFormButtonSettings(options?.texts?.buttonLabel ?? '', options?.buttonIcon ?? ''),
    modalFormButtonSettings: createFormButtonSettings(
        options?.texts?.modalButtonLabel ?? '',
        options?.buttonIcon ?? ''
    ),
    additionalFormSettings: {
        actionAfterConfirmType: 'message',
        subscriptionMessage: options?.texts?.subscriptionMessageHtml ?? '',
        updateExistingContact: false,
        openLink: '',
        isEnabledCaptcha: true,
        captchaLimit: 100,
    },
    analyticsSettings: createAnalyticsSettingsLegacy({
        googleSettings: {
            category: null,
            action: null,
            label: null,
            currency: null,
            value: null,
            leadSource: options?.texts?.leadSource ?? null,
        },
        pixelSettings: {
            type: null,
        },
    }),
    hideOnMobile: false,
    hideOnTablet: false,
    hideOnDesktop: false,
    isSynchronizeStyle: true,
    formView: 'static',
    modalTitle: options?.texts?.modalTitle ?? '',
    isTwoStepForm: false,
});

const createDefaultData = (options?: CreateLegacyFormWidgetOptions): LegacyFormDefault['data'] => ({
    ...createCommonData(options),
    position: createWidgetPositionLegacy({
        x: 0,
        y: 0,
        zIndex: 1,
        rotate: 0,
        mobileX: 0,
        mobileY: 0,
        mobileZIndex: 1,
        mobileRotate: 0,
        width: 100,
    }),
});

const createZeroData = (options?: CreateLegacyFormWidgetOptions): LegacyFormZero['data'] => ({
    ...createCommonData(options),
    // Zero blocks are laid out per-device — start with sync off (button precedent).
    isSynchronizeStyle: false,
    // Height is a placeholder: the form view DOM-syncs the rendered content
    // height into the grid (button's `setupZeroHeightSync` pattern).
    position: createExtendedPositionLegacy({
        width: 400,
        tabletWidth: 400,
        mobileWidth: 320,
        height: 360,
        tabletHeight: 360,
        mobileHeight: 360,
    }),
});

export type LegacyFormWidgetVariant = 'default' | 'zero';

/**
 * Factory for the current legacy form-widget entity — matches the persisted
 * widget JSON shape (`{ id, name: 'form', columnId, data, … }`) and the
 * host model's `formData()` defaults verbatim. Variant selects between
 * default-column placement (no `type` field, simple position grid) and
 * zero-block placement (`type: 'zero'`, extended-position grid).
 */
export function createLegacyFormWidget(variant?: 'default', options?: CreateLegacyFormWidgetOptions): LegacyFormDefault;
export function createLegacyFormWidget(variant: 'zero', options?: CreateLegacyFormWidgetOptions): LegacyFormZero;
export function createLegacyFormWidget(
    variant: LegacyFormWidgetVariant = 'default',
    options?: CreateLegacyFormWidgetOptions
): LegacyFormWidget {
    if (variant === 'zero') {
        return {
            id: '',
            name: 'form',
            columnId: '',
            type: 'zero',
            data: createZeroData(options),
        };
    }

    return {
        id: '',
        name: 'form',
        columnId: '',
        data: createDefaultData(options),
    };
}

/**
 * Convenience entry-point that returns whichever shape is the current
 * default for new form widgets. While only `legacy` exists, this delegates
 * to {@link createLegacyFormWidget}; pass the variant to opt into the
 * zero-block tag.
 */
export function createFormWidget(variant?: 'default', options?: CreateLegacyFormWidgetOptions): LegacyFormDefault;
export function createFormWidget(variant: 'zero', options?: CreateLegacyFormWidgetOptions): LegacyFormZero;
export function createFormWidget(
    variant: LegacyFormWidgetVariant = 'default',
    options?: CreateLegacyFormWidgetOptions
): FormWidgetEntity {
    // The overloads on `createLegacyFormWidget` are picked per literal arg —
    // calling each branch separately keeps overload resolution happy.
    return variant === 'zero' ? createLegacyFormWidget('zero', options) : createLegacyFormWidget('default', options);
}
